import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo } from "react";
import { formatDistanceToNow } from "date-fns";
import { Check, X, Users, Calendar, Vote, Bell, UserPlus } from "lucide-react";
import { useQuestingGroups } from "../../hooks/useQuestingGroups";
import { useFriends } from "../../hooks/useFriends";
import { usePollInvites } from "../../hooks/usePollInvites";
import { NOTIFICATION_TYPES, normalizeNotificationType } from "../../lib/data/notifications";
import { useUserProfiles, useUserProfilesByIds } from "../../hooks/useUserProfiles";
import { UserIdentity } from "../UserIdentity";
import { normalizeEmail } from "../../lib/utils";

const ACTOR_RENDERED_NOTIFICATION_TYPES = new Set([
  NOTIFICATION_TYPES.FRIEND_REQUEST,
  NOTIFICATION_TYPES.FRIEND_ACCEPTED,
  NOTIFICATION_TYPES.POLL_INVITE,
  NOTIFICATION_TYPES.SESSION_INVITE,
  NOTIFICATION_TYPES.GROUP_INVITE,
  NOTIFICATION_TYPES.GROUP_INVITE_ACCEPTED,
  NOTIFICATION_TYPES.SESSION_JOINED,
  NOTIFICATION_TYPES.VOTE_SUBMITTED,
]);

function notificationUsesActor(notification) {
  return ACTOR_RENDERED_NOTIFICATION_TYPES.has(normalizeNotificationType(notification?.type));
}

function NotificationIcon({ type }) {
  const normalizedType = normalizeNotificationType(type);
  switch (normalizedType) {
    case NOTIFICATION_TYPES.FRIEND_REQUEST:
      return <UserPlus className="h-4 w-4 text-indigo-500" />;
    case NOTIFICATION_TYPES.FRIEND_ACCEPTED:
      return <Users className="h-4 w-4 text-emerald-500" />;
    case NOTIFICATION_TYPES.POLL_INVITE:
      return <Calendar className="h-4 w-4 text-amber-500" />;
    case NOTIFICATION_TYPES.GROUP_INVITE:
      return <Users className="h-4 w-4 text-purple-500" />;
    case NOTIFICATION_TYPES.GROUP_INVITE_ACCEPTED:
      return <Users className="h-4 w-4 text-emerald-500" />;
    case NOTIFICATION_TYPES.SESSION_INVITE:
      return <Calendar className="h-4 w-4 text-blue-500" />;
    case NOTIFICATION_TYPES.SESSION_FINALIZED:
      return <Calendar className="h-4 w-4 text-emerald-500" />;
    case NOTIFICATION_TYPES.VOTE_REMINDER:
      return <Vote className="h-4 w-4 text-amber-500" />;
    case NOTIFICATION_TYPES.SESSION_UPCOMING_REMINDER:
      return <Calendar className="h-4 w-4 text-violet-500" />;
    case NOTIFICATION_TYPES.VOTE_SUBMITTED:
      return <Vote className="h-4 w-4 text-emerald-500" />;
    case NOTIFICATION_TYPES.GROUP_MEMBER_CHANGE:
      return <Users className="h-4 w-4 text-blue-500" />;
    case NOTIFICATION_TYPES.SESSION_JOINED:
      return <Users className="h-4 w-4 text-indigo-500" />;
    default:
      return <Bell className="h-4 w-4 text-slate-500" />;
  }
}

function NotificationItem({
  notification,
  onMarkRead,
  onDismiss,
  onNavigate,
  onAcceptGroupInvite,
  onDeclineGroupInvite,
  onAcceptFriendRequest,
  onDeclineFriendRequest,
  onAcceptPollInvite,
  onDeclinePollInvite,
  onRemoveLocal,
  resolveActor,
}) {
  const normalizedType = normalizeNotificationType(notification.type);
  const isGroupInvite = normalizedType === NOTIFICATION_TYPES.GROUP_INVITE;
  const isFriendRequest = normalizedType === NOTIFICATION_TYPES.FRIEND_REQUEST;
  const isPollInvite = normalizedType === NOTIFICATION_TYPES.POLL_INVITE;
  const isActionNotification = isGroupInvite || isFriendRequest || isPollInvite;
  const isInteractive = !isActionNotification && (!notification.read || notification.actionUrl);
  const actionLabel = isFriendRequest
    ? "friend request"
    : isPollInvite
      ? "poll invitation"
      : "group invitation";
  const timeAgo = notification.createdAt?.toDate
    ? formatDistanceToNow(notification.createdAt.toDate(), { addSuffix: true })
    : "";

  const handleClick = () => {
    if (!notification.read) {
      onMarkRead(notification.id);
    }
    if (notification.actionUrl && !isGroupInvite && !isFriendRequest && !isPollInvite) {
      onNavigate(notification.actionUrl);
    }
  };

  const handleAccept = async (e) => {
    e.stopPropagation();
    try {
      if (isGroupInvite && notification.metadata?.groupId) {
        await onAcceptGroupInvite(notification.metadata.groupId);
      }
      if (isFriendRequest && notification.metadata?.requestId) {
        await onAcceptFriendRequest(notification.metadata.requestId);
      }
      if (isPollInvite && notification.metadata?.schedulerId) {
        await onAcceptPollInvite(notification.metadata.schedulerId);
        onNavigate(`/scheduler/${notification.metadata.schedulerId}`);
      }
    } finally {
      onRemoveLocal?.(notification.id);
    }
  };

  const handleDecline = async (e) => {
    e.stopPropagation();
    try {
      if (isGroupInvite && notification.metadata?.groupId) {
        await onDeclineGroupInvite(notification.metadata.groupId);
      }
      if (isFriendRequest && notification.metadata?.requestId) {
        await onDeclineFriendRequest(notification.metadata.requestId);
      }
      if (isPollInvite && notification.metadata?.schedulerId) {
        await onDeclinePollInvite(notification.metadata.schedulerId);
      }
    } finally {
      onRemoveLocal?.(notification.id);
    }
  };

  const actor = notificationUsesActor(notification) ? resolveActor(notification) : null;
  const actorNode = actor ? <UserIdentity user={actor} /> : null;
  const metadata = notification.metadata || {};

  const renderBody = () => {
    const schedulerTitle = metadata.schedulerTitle || notification.resource?.title;
    const groupName = metadata.groupName || notification.resource?.title;
    switch (normalizedType) {
      case NOTIFICATION_TYPES.FRIEND_REQUEST:
        return actorNode ? (
          <>
            {actorNode} sent you a friend request
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.FRIEND_ACCEPTED:
        return actorNode ? (
          <>
            {actorNode} accepted your friend request
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.POLL_INVITE:
        return actorNode && schedulerTitle ? (
          <>
            {actorNode} invited you to join "{schedulerTitle}"
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.SESSION_INVITE:
        return actorNode && schedulerTitle ? (
          <>
            {actorNode} invited you to vote on "{schedulerTitle}"
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.GROUP_INVITE:
        return actorNode && groupName ? (
          <>
            {actorNode} invited you to join "{groupName}"
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.GROUP_INVITE_ACCEPTED:
        return actorNode && groupName ? (
          <>
            {actorNode} accepted your invite to "{groupName}"
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.SESSION_JOINED:
        return actorNode && schedulerTitle ? (
          <>
            {actorNode} joined "{schedulerTitle}"
          </>
        ) : (
          notification.body
        );
      case NOTIFICATION_TYPES.VOTE_SUBMITTED:
        return actorNode && schedulerTitle ? (
          <>
            {actorNode} updated votes for "{schedulerTitle}"
          </>
        ) : (
          notification.body
        );
      default:
        return notification.body;
    }
  };

  const notificationContent = (
    <>
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
        <NotificationIcon type={notification.type} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
          {notification.title}
        </p>
        <p className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">
          {renderBody()}
        </p>
        {isActionNotification && (
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleAccept}
              className="flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-emerald-600"
              aria-label={`Accept ${actionLabel}`}
            >
              <Check className="h-3 w-3" />
              Accept
            </button>
            <button
              type="button"
              onClick={handleDecline}
              className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
              aria-label={`Decline ${actionLabel}`}
            >
              <X className="h-3 w-3" />
              Decline
            </button>
          </div>
        )}
        <p className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">{timeAgo}</p>
      </div>
    </>
  );

  return (
    <div
      className={`flex border-b border-slate-200/70 transition-colors last:border-b-0 dark:border-slate-700 ${
        notification.read
          ? "bg-white dark:bg-slate-800"
          : "bg-blue-50/50 dark:bg-blue-900/20"
      } ${isInteractive ? "hover:bg-slate-50 dark:hover:bg-slate-700/50" : ""}`}
    >
      {isInteractive ? (
        <button
          type="button"
          onClick={handleClick}
          className="flex min-w-0 flex-1 cursor-pointer gap-3 p-3 pr-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary"
        >
          {notificationContent}
        </button>
      ) : (
        <div className="flex min-w-0 flex-1 gap-3 p-3 pr-1">{notificationContent}</div>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(notification.id);
        }}
        className="mr-3 mt-3 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-300"
        aria-label="Dismiss notification"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

export function NotificationDropdown({
  notifications,
  loading,
  onMarkRead,
  onDismiss,
  onMarkAllRead,
  onDismissAll,
  onRemoveLocal,
}) {
  const navigate = useNavigate();
  const {
    acceptInvite,
    declineInvite,
    pendingInvites: pendingGroupInvites,
    loading: groupsLoading,
  } = useQuestingGroups();
  const {
    acceptFriendRequest,
    declineFriendRequest,
    incomingRequests,
    loading: friendsLoading,
  } = useFriends();
  const {
    acceptInvite: acceptPollInvite,
    declineInvite: declinePollInvite,
    pendingInvites: pendingPollInvites,
    loading: pollInvitesLoading,
  } = usePollInvites();

  const pendingFriendRequestIds = useMemo(
    () => new Set((incomingRequests || []).map((request) => request.id).filter(Boolean)),
    [incomingRequests]
  );
  const pendingGroupInviteIds = useMemo(
    () => new Set((pendingGroupInvites || []).map((invite) => invite.id).filter(Boolean)),
    [pendingGroupInvites]
  );
  const pendingPollInviteIds = useMemo(
    () => new Set((pendingPollInvites || []).map((invite) => invite.id).filter(Boolean)),
    [pendingPollInvites]
  );

  const actorUserIds = useMemo(
    () =>
      notifications
        .filter(notificationUsesActor)
        .map((notification) => {
          const meta = notification.metadata || {};
          return (
            notification.actor?.uid ||
            meta.actorUserId ||
            meta.fromUserId ||
            meta.inviterUserId ||
            meta.friendUserId ||
            meta.voterUserId ||
            meta.participantUserId ||
            meta.memberUserId ||
            null
          );
        })
        .filter(Boolean),
    [notifications]
  );

  const actorEmails = useMemo(
    () =>
      notifications
        .filter(notificationUsesActor)
        .map((notification) => {
          const meta = notification.metadata || {};
          return (
            notification.actor?.email ||
            meta.actorEmail ||
            meta.fromEmail ||
            meta.inviterEmail ||
            meta.friendEmail ||
            meta.voterEmail ||
            meta.participantEmail ||
            meta.memberEmail ||
            null
          );
        })
        .filter(Boolean),
    [notifications]
  );

  const { profiles: profilesById } = useUserProfilesByIds(actorUserIds);
  const { profiles: profilesByEmail } = useUserProfiles(actorEmails);

  const handleNavigate = (url) => {
    navigate(url);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  useEffect(() => {
    if (loading || friendsLoading || groupsLoading || pollInvitesLoading) return;
    if (!notifications.length) return;

    notifications.forEach((notification) => {
      const normalizedType = normalizeNotificationType(notification.type);
      if (normalizedType === NOTIFICATION_TYPES.FRIEND_REQUEST) {
        const requestId = notification.metadata?.requestId || notification.resource?.id;
        if (requestId && !pendingFriendRequestIds.has(requestId)) {
          onDismiss(notification.id);
        }
      }
      if (normalizedType === NOTIFICATION_TYPES.GROUP_INVITE) {
        const groupId = notification.metadata?.groupId || notification.resource?.id;
        if (groupId && !pendingGroupInviteIds.has(groupId)) {
          onDismiss(notification.id);
        }
      }
      if (normalizedType === NOTIFICATION_TYPES.POLL_INVITE) {
        const schedulerId = notification.metadata?.schedulerId || notification.resource?.id;
        if (schedulerId && !pendingPollInviteIds.has(schedulerId)) {
          onDismiss(notification.id);
        }
      }
    });
  }, [
    loading,
    friendsLoading,
    groupsLoading,
    pollInvitesLoading,
    notifications,
    pendingFriendRequestIds,
    pendingGroupInviteIds,
    pendingPollInviteIds,
    onDismiss,
  ]);

  const resolveActor = useCallback(
    (notification) => {
      const meta = notification?.metadata || {};
      const actorUserId =
        notification?.actor?.uid ||
        meta.actorUserId ||
        meta.fromUserId ||
        meta.inviterUserId ||
        meta.friendUserId ||
        meta.voterUserId ||
        meta.participantUserId ||
        meta.memberUserId ||
        null;
      const actorEmail =
        notification?.actor?.email ||
        meta.actorEmail ||
        meta.fromEmail ||
        meta.inviterEmail ||
        meta.friendEmail ||
        meta.voterEmail ||
        meta.participantEmail ||
        meta.memberEmail ||
        null;

      if (actorUserId && profilesById[actorUserId]) {
        return profilesById[actorUserId];
      }
      if (actorEmail) {
        const normalized = normalizeEmail(actorEmail);
        // Only return a resolved profile (which carries the chosen public
        // identifier). Never synthesize a bare { email } actor — that would
        // render the raw address. When the profile hasn't resolved, return null
        // so the body falls back to the safe server-rendered notification text.
        return profilesByEmail[normalized] || null;
      }
      return null;
    },
    [profilesByEmail, profilesById]
  );

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200/70 px-3 py-2 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
              ({unreadCount} unread)
            </span>
          )}
        </h3>
        {notifications.length > 0 && (
          <div className="flex gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="text-xs text-brand-primary hover:underline"
              >
                Mark all read
              </button>
            )}
            <button
              type="button"
              onClick={onDismissAll}
              className="text-xs text-slate-500 hover:text-slate-700 hover:underline dark:text-slate-400 dark:hover:text-slate-300"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Notification list */}
      <div className="max-h-80 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-primary border-t-transparent" />
          </div>
        )}

        {!loading && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Bell className="h-8 w-8 text-slate-300 dark:text-slate-600" />
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              No notifications
            </p>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              You're all caught up!
            </p>
          </div>
        )}

        {!loading &&
          notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkRead={onMarkRead}
              onDismiss={onDismiss}
              onNavigate={handleNavigate}
              onAcceptGroupInvite={acceptInvite}
              onDeclineGroupInvite={declineInvite}
              onAcceptFriendRequest={acceptFriendRequest}
              onDeclineFriendRequest={declineFriendRequest}
              onAcceptPollInvite={acceptPollInvite}
              onDeclinePollInvite={declinePollInvite}
              onRemoveLocal={onRemoveLocal}
              resolveActor={resolveActor}
            />
          ))}
      </div>
    </div>
  );
}
